import { Calendar, Mail, Phone, Users, X } from "lucide-react"
import { useState } from "react"
import type { ClientActivity } from "../types"

type AddActivityModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (activity: Omit<ClientActivity, "id" | "date">) => void
  clientName: string
}

const activityTypes: Array<{
  value: ClientActivity["type"]
  label: string
  icon: typeof Phone
}> = [
  { value: "call", label: "Call", icon: Phone },
  { value: "email", label: "Email", icon: Mail },
  { value: "meeting", label: "Meeting", icon: Users },
  { value: "note", label: "Note", icon: Calendar },
]

const AddActivityModal = ({ isOpen, onClose, onSave, clientName }: AddActivityModalProps) => {
  const [formData, setFormData] = useState({
    type: "call" as ClientActivity["type"],
    summary: "",
    author: "",
    metadata: {} as Record<string, unknown>,
  })

  const [metadataFields, setMetadataFields] = useState<Array<{ key: string; value: string }>>([])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const metadata: Record<string, unknown> = { ...formData.metadata }
    metadataFields.forEach((field) => {
      if (field.key && field.value) {
        metadata[field.key] = field.value
      }
    })

    const newActivity: Omit<ClientActivity, "id" | "date"> = {
      type: formData.type,
      summary: formData.summary,
      author: formData.author || undefined,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    }

    onSave(newActivity)
    handleClose()
  }

  const handleClose = () => {
    setFormData({
      type: "call",
      summary: "",
      author: "",
      metadata: {},
    })
    setMetadataFields([])
    onClose()
  }

  const addMetadataField = () => {
    setMetadataFields((prev) => [...prev, { key: "", value: "" }])
  }

  const updateMetadataField = (index: number, field: "key" | "value", value: string) => {
    setMetadataFields((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  const removeMetadataField = (index: number) => {
    setMetadataFields((prev) => prev.filter((_, i) => i !== index))
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-lg rounded-[24px] border border-line bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line p-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Add Activity</h2>
            <p className="mt-1 text-sm text-stone">{clientName}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-stone hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Activity Type */}
            <div>
              <label className="mb-3 block text-sm font-medium text-slate-900">Activity Type</label>
              <div className="grid grid-cols-2 gap-3">
                {activityTypes.map((type) => {
                  const Icon = type.icon
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, type: type.value }))}
                      className={`
                        flex items-center gap-3 rounded-xl border-2 p-4 text-left transition
                        ${
                          formData.type === type.value
                            ? "border-moss bg-moss/5"
                            : "border-line bg-white hover:border-slate-300"
                        }
                      `}
                    >
                      <div
                        className={`
                          flex h-10 w-10 items-center justify-center rounded-lg
                          ${
                            formData.type === type.value
                              ? "bg-moss text-white"
                              : "bg-slate-100 text-slate-600"
                          }
                        `}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="font-medium text-slate-900">{type.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Summary */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-900">
                Summary *
              </label>
              <textarea
                required
                value={formData.summary}
                onChange={(e) => setFormData((prev) => ({ ...prev, summary: e.target.value }))}
                className="w-full resize-none rounded-lg border border-line bg-white p-3 text-sm outline-none focus:border-moss"
                rows={3}
                placeholder="Brief description of the activity..."
              />
            </div>

            {/* Author */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-900">Author</label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => setFormData((prev) => ({ ...prev, author: e.target.value }))}
                className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                placeholder="Your name (optional)"
              />
            </div>

            {/* Metadata (for calls, meetings) */}
            {(formData.type === "call" || formData.type === "meeting") && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-900">Additional Details</label>
                  <button
                    type="button"
                    onClick={addMetadataField}
                    className="text-xs text-moss hover:underline"
                  >
                    + Add Field
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.type === "call" && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Duration (e.g., 30m)"
                          className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, duration: e.target.value },
                            }))
                          }
                        />
                        <input
                          type="text"
                          placeholder="Topic"
                          className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, topic: e.target.value },
                            }))
                          }
                        />
                      </div>
                    </>
                  )}
                  {formData.type === "meeting" && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Duration (e.g., 60m)"
                          className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, duration: e.target.value },
                            }))
                          }
                        />
                        <input
                          type="text"
                          placeholder="Attendees"
                          className="rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, attendees: e.target.value.split(",") },
                            }))
                          }
                        />
                      </div>
                    </>
                  )}
                  {metadataFields.map((field, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={field.key}
                        onChange={(e) => updateMetadataField(index, "key", e.target.value)}
                        placeholder="Field name"
                        className="flex-1 rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                      />
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => updateMetadataField(index, "value", e.target.value)}
                        placeholder="Value"
                        className="flex-1 rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                      />
                      <button
                        type="button"
                        onClick={() => removeMetadataField(index)}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-stone hover:bg-slate-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-end gap-3 border-t border-line pt-6">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
            >
              Add Activity
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddActivityModal
